import React, { useEffect } from 'react';
/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useNavigate } from 'react-router-dom';

// utils + actions
import { getParam } from '../../common/utils/qs';
import { setAuthFromCookie, validateSession } from '../../actions/session';
import { getAgent, getShopper } from '../../actions/user';
import {
  connectSSO,
  initCCP,
  resetIfTimeout,
} from '../../actions/telephony';
import { getCookie, setCookie } from '../../common/utils/cookie';
import { fmtAgent, fmtShopper } from '../../common/utils/format';

// components
import Softphone from './Softphone';

function Auth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // -- set a login timeout defaulted to 30 seconds, modifiable by theme var
  const {
    connectLoginTimeout = 30,
    agentPortal: agentPortalUrl,
    softphoneTitle = 'MMB Softphone',
  } = useSelector(store => store.theme);
  const { agentState, loginAttempt } = useSelector(store => store.telephony);
  const { SAMLResponse } = useSelector(store => store.iframe);

  useEffect(() => {
    (async () => {
      dispatch({ type: 'CLEAR' });
      setTimeout(() => localStorage.setItem('hasClearedRedux', true), 100);

      setCookie('softphoneQuoteId', '');
      document.title = softphoneTitle;
      dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { errmsg: undefined } });
      try {
        // use cookie to store cross window state that can be read from
        //  other open instances of enrollment portal
        setCookie('softphoneOpen', 'true');
        window.addEventListener('beforeunload', () => setCookie('softphoneOpen', 'false'));

        // get auth token and load session data
        await setAuthFromCookie();
        const phoneToCall = getParam('p');
        const shopperUsername = getParam('s');
        const effectiveDate = getParam('e');
        if (!shopperUsername) {
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { errmsg: 'missing param s' } });
          navigate('/softphone/error');
        } else if (!phoneToCall) {
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { errmsg: 'missing param p' } });
          navigate('/softphone/error');
        } else if (!effectiveDate) {
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { errmsg: 'missing param e' } });
          navigate('/softphone/error');
        } else {
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { phone: phoneToCall } });
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { shopperUsername } });
          dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { effectiveDate } });
        }
        await validateSession();
        const rawAgent = await getAgent();
        const rawShopper = await getShopper(shopperUsername);

        const agent = fmtAgent(rawAgent);
        const shopper = fmtShopper(rawShopper);

        const {
          phoneInfo,
          username: agentUsername,
        } = agent;
        let { instanceDomain } = (phoneInfo || {});

        const shopperName = `${shopper.firstName} ${shopper.lastName}`;
        dispatch({ type: 'SET_AGENT', agent });
        dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { shopperName } });

        // Auto login to connect
        setTimeout(() => {
          resetIfTimeout()
            .catch(() => {
              const errmsg = 'There was a problem with the authentication process. Please close the softphone window and try again.';
              dispatch({
                type: 'SET_SOFTPHONE_FIELD',
                payload: { errmsg },
              });
              navigate('/softphone/error');
            });
        }, connectLoginTimeout * 1000);

        // -- get the saml login data from api
        const samlData = await connectSSO(agentUsername);
        // const base = window.location.origin;
        // const destinationURL = encodeURIComponent(samlData.destination_url);
        // const relayState = encodeURIComponent(samlData.RelayState);
        // const samlResponse = encodeURIComponent(samlData.SAMLResponse);
        // const samlURL = `${base}/saml?destinationURL=${destinationURL}&RelayState=${relayState}&SAMLResponse=${samlResponse}`

        // if the user was not provisioned previously it was provisioned this time;
        // if that is the case we need to reload the agent data to include their
        // agent_phone_info data.
        if (!phoneInfo) {
          const a = await getAgent();
          const formattedAgent = fmtAgent(a);
          ({ phoneInfo: { instanceDomain } } = formattedAgent);
          dispatch({ type: 'LOAD_AGENT', data: formattedAgent });
        }
        dispatch({ type: 'SET_CONNECT_LOGIN_STATUS', payload: true });

        // if the effective date of the shopper is modified in the agent portal
        // during a call we should make sure that the effective date reflects
        // the last selected effective date on agent portal prior to the call ending.
        // This will listen for effective date updates via `window.postMessage`
        // on agent portal.
        window.addEventListener('message', (event) => {
          if (event.origin !== agentPortalUrl) return;
          const { data: { effectiveDate: ed } } = event;
          if (ed) {
            dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { effectiveDate: ed } });
          }
        }, false);

        // we need to set an interval to check to see if an enrollment is opened
        // during a call. If an enrollmnet is opened during a call we need to
        // associate the quote with the call record, so we send it to redux
        // so that the quote id can be used to update the call record when
        // the call ends. See src/components/FormLoader.jsx for where this cookie
        // gets set.
        setInterval(() => {
          const quoteID = getCookie('softphoneQuoteId');
          if (quoteID) {
            dispatch({ type: 'SET_SOFTPHONE_FIELD', payload: { softphoneQuoteId: quoteID } });
          }
        }, 1000);

        await initCCP(samlData, instanceDomain);

      } catch (err) {
        console.log(err);
        navigate('/softphone/error');
      }
    })();
  }, []);

  const hasClearedRedux = localStorage.getItem('hasClearedRedux');
  console.log('render... | loginAttempt: ', loginAttempt, ' | agentState: ', agentState, ' | hasClearedRedux: ', hasClearedRedux);
  if (hasClearedRedux
    && loginAttempt
    && agentState === 'Available'
    && !SAMLResponse
  ) {
    // delay navigation so component has time to rerender to avoid react error
    localStorage.setItem('hasClearedRedux', '');
    setTimeout(() => navigate('/softphone'), 200);
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="d-flex col mt-4 justify-content-center">
          <i>Loading telephony system...</i>
        </div>
        <div className="w-100 mt-4" />
        <div className="d-flex col justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Error() {
  const { errmsg } = useSelector(store => store.softphone);
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="d-flex col justify-content-center my-4">
          {
            errmsg
            || 'Something went wrong. Please close the window and try again.'
          }
        </div>
      </div>
    </div>
  );
}

function Router() {
  return (
    <div>
      <Routes>
        {/* querystring 'phone' should be sent on /auth */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/error" element={<Error />} />
        <Route path="" element={<Softphone />} />
      </Routes>
    </div>
  );
}

export default Router;
